import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, []);

  const defaultFaqs = [
    {
      faq_id: 'default-1',
      question: 'What classes does STUDY POINT cover?',
      answer: 'STUDY POINT provides comprehensive educational content for students from class 6th to 12th, including all major streams for senior secondary (Commerce, Science, Biology, and Humanities).',
    },
    {
      faq_id: 'default-2',
      question: 'How can I access the study materials?',
      answer: 'Simply create an account using your Google account, browse through the classes section, and access videos, PDFs, PPTs, and other study materials. Some content is free while premium content may require payment.',
    },
    {
      faq_id: 'default-3',
      question: 'What is Dauter AI?',
      answer: 'Dauter AI is our intelligent study assistant powered by GPT-5.2. It can help you with homework, explain concepts, assist with exam preparation, and provide career guidance. Your chat history is saved to your account.',
    },
    {
      faq_id: 'default-4',
      question: 'How do I make a payment for premium content?',
      answer: 'We accept UPI payments. Simply scan the QR code or use the UPI ID provided on the payment page. After payment, share the transaction ID with us for verification.',
    },
    {
      faq_id: 'default-5',
      question: 'Can I download the study materials?',
      answer: 'To protect our content, downloading is not available. However, you can access all materials online anytime from your account.',
    },
    {
      faq_id: 'default-6',
      question: 'How can I contact STUDY POINT?',
      answer: 'You can reach us through WhatsApp at +91 97167 94909, +91 96255 83120, or +91 7827272198. You can also use our feedback form or visit our contact page for more options.',
    },
  ];

  const displayFaqs = faqs.length > 0 ? faqs : defaultFaqs;

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Support</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
            Frequently Asked <span className="text-red-500">Questions</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
            Find answers to common questions about STUDY POINT and our services.
          </p>
        </div>

        {/* FAQ Accordion */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : (
          <Accordion type="single" collapsible className="space-y-4">
            {displayFaqs.map((faq, idx) => (
              <AccordionItem
                key={faq.faq_id}
                value={faq.faq_id}
                className={`card-hover bg-[#121212] rounded-sm border-none animate-fadeIn stagger-${(idx % 5) + 1}`}
                data-testid={`faq-${faq.faq_id}`}
              >
                <AccordionTrigger className="px-6 py-4 text-left text-white hover:no-underline hover:text-red-500 transition-colors">
                  <span className="font-semibold">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-zinc-400 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {/* Contact CTA */}
        <div className="mt-12 text-center p-8 bg-[#121212] rounded-sm border border-zinc-800">
          <h3 className="font-outfit text-xl font-bold text-white mb-2">Still have questions?</h3>
          <p className="text-zinc-400 mb-4">Can't find what you're looking for? Feel free to reach out.</p>
          <a
            href="https://wa.me/919716794909"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-sm transition-colors"
            data-testid="contact-whatsapp-btn"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
